import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { X, ShoppingCart } from "@phosphor-icons/react";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { ExitCouponNudgeConfigType } from "lib/types/checkout";
import { useApplyDiscount } from "lib/hooks/useApplyDiscount";
import { Icon } from "@shopflo/ui/icon";
import { withScope } from "@sentry/react";
import { getRequest } from "lib/core/apiClient";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { getExitCouponNudgeConfig } from "lib/utils/checkout";
import DotsLoader from "../loaders/DotsLoader";

// Returns: true = coupon config applied (show popup), false = no coupon available (close checkout), null = not exit-coupon mode
// Subsequent calls within the same checkout session return the cached result so the API is called only once,
// but the caller still gets the correct true/false to decide whether to open the popup or close immediately.
export function useFetchExitCoupon() {
  const {
    state: { checkoutId },
    actions: { setExitCouponNudgeRuntime },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const exitCheckoutAction = checkoutMetadata?.uiAttributes?.exitCheckoutAction;

  // null = not yet fetched, true/false = cached result from first fetch this session
  const exitCouponFetchResultRef = useRef<boolean | null>(null);

  useEffect(() => {
    exitCouponFetchResultRef.current = null;
  }, [checkoutId]);

  return useCallback(async (): Promise<boolean | null> => {
    if (exitCheckoutAction !== "exit_coupon_nudge") return null;
    // Return cached result so caller always gets true/false, never re-fetches mid-session
    if (exitCouponFetchResultRef.current !== null) return exitCouponFetchResultRef.current;
    if (!checkoutId) return false;

    try {
      const data = await getRequest(
        `/checkout/${checkoutId}/exit-coupon`,
        isAuthenticated ? "KRATOS_PRIVATE" : "KRATOS_PUBLIC",
      );
      const visualConfig = checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig;
      const couponDetails = data?.coupon_details;
      const applicable: boolean = data?.applicable ?? false;
      const alreadyApplied: boolean = !applicable && data?.un_applicability_reason?.reason === "UARC_001";
      if (!applicable && !alreadyApplied) {
        setExitCouponNudgeRuntime(undefined);
        exitCouponFetchResultRef.current = false;
        return false;
      }
      const couponCode: string = couponDetails?.coupon_code ?? visualConfig?.couponCode ?? "";
      if (!couponCode) {
        setExitCouponNudgeRuntime(undefined);
        exitCouponFetchResultRef.current = false;
        return false;
      }
      const couponValue: string = couponDetails?.header ?? visualConfig?.couponValue ?? "";
      // Only the API-resolved pricing/eligibility lives in CheckoutProvider; the visual config
      // stays in ui-attributes and is merged in at render via getExitCouponNudgeConfig.
      setExitCouponNudgeRuntime({
        couponCode,
        couponValue,
        concessionAmount: couponDetails?.concession_amount ?? 0,
        amountType: (couponDetails?.deduction_type as "FLAT" | "PERCENTAGE") ?? "FLAT",
        alreadyApplied,
      });
      exitCouponFetchResultRef.current = true;
      return true;
    } catch (err) {
      withScope((scope) => {
        scope.setTag("feature", "exit_coupon_nudge");
        scope.captureMessage("exit-coupon API failed; falling back to dashboard config", "warning");
      });
      const visualConfig = checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig;
      if (visualConfig?.couponCode?.trim()) {
        exitCouponFetchResultRef.current = true;
        setExitCouponNudgeRuntime({
          concessionAmount: 0,
          amountType: "FLAT",
          couponCode: visualConfig.couponCode,
          couponValue: visualConfig.couponValue,
        });
        return true;
      }
      exitCouponFetchResultRef.current = false;
      return false;
    }
  }, [
    exitCheckoutAction,
    checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig,
    checkoutId,
    isAuthenticated,
    setExitCouponNudgeRuntime,
  ]);
}

interface ExitCouponNudgePopupProps {
  isOpen: boolean;
  closePopup: () => void;
  onLeaveAttempt: () => void;
}

const ExitCouponNudgePopup: React.FC<ExitCouponNudgePopupProps> = ({
  isOpen,
  closePopup,
  onLeaveAttempt,
}) => {
  const { t } = useLocale();

  const {
    state: { exitCouponNudgeRuntime, billing },
    actions: { setExitCouponNudgeRuntime },
  } = useCheckoutContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const { applyDiscount } = useApplyDiscount();

  const [isLoading, setIsLoading] = useState(false);

  const config = getExitCouponNudgeConfig(
    checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig,
    exitCouponNudgeRuntime,
  );
  const couponCode = config?.couponCode?.trim();

  const handleApplyAndStay = useCallback(async () => {
    if (!couponCode) {
      closePopup();
      return;
    }
    setIsLoading(true);
    await applyDiscount(couponCode);
    setIsLoading(false);
    // Clear runtime so a subsequent back-press shows plain exit popup instead of re-offering the used coupon
    setExitCouponNudgeRuntime(undefined);
    closePopup();
  }, [couponCode, applyDiscount, closePopup, setExitCouponNudgeRuntime]);

  if (!config) return null;

  const currentPayable = billing?.total_payable ?? 0;

  const estimatedNewTotal = (() => {
    if (!config?.concessionAmount || !currentPayable) return null;
    if (config.amountType === "PERCENTAGE") {
      const discount = Math.round((currentPayable * config.concessionAmount) / 100);
      const capped = config.maximumConcessionAmount
        ? Math.min(discount, config.maximumConcessionAmount)
        : discount;
      return currentPayable - capped;
    }
    return Math.max(0, currentPayable - config.concessionAmount);
  })();

  const mrpTotal = currentPayable;
  const payableTotal = estimatedNewTotal ?? currentPayable;

  return (
    <>
      <GenericDialog
        isOpen={isOpen}
        setIsOpen={closePopup}
        translateAxis="y"
        dialogOverlay
        modalType="EXIT_CONFIRMATION"
        customClass="!overflow-visible md:!top-auto md:absolute rounded-t-2xl !p-0 max-h-[85vh]">
        <div className="relative flex flex-col items-stretch antialiased">
          {/* Badge — anchored to outer wrapper so overflow-hidden on card doesn't clip it */}
          {config.badge.htmlText && (
            <div className="absolute top-0 left-5 z-10 -translate-y-1/2">
              <span
                className="rounded-full text-text-xs-semibold"
                style={{
                  padding: "4px 12px",
                  backgroundColor: config.badge.bgColor,
                  color: config.badge.textColor,
                }}
                dangerouslySetInnerHTML={{ __html: config.badge.htmlText }}
              />
            </div>
          )}

          <div className="relative z-0 bg-white flex flex-col rounded-t-2xl overflow-hidden antialiased">
            {/* Gradient hero */}
            <div className="relative overflow-hidden">
              {/* Spinning conic gradient */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute z-0 left-[15%] top-[64%] -translate-x-1/2 -translate-y-1/2">
                <div
                  className="w-[800px] h-[800px] will-change-transform animate-exit-coupon-spin"
                  style={{
                    background: `conic-gradient(from 195deg at 50% 50%, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 0deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 47deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 96deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 121deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 142deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 161deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 181deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 198deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 223deg, transparent 245deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 260deg, rgba(255,255,255,0) 282deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 310deg, rgba(255,255,255,0) 334deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 360deg)`,
                  }}
                />
              </div>

              {/* Radial fade overlay + content */}
              <div
                className="relative z-10 flex w-full flex-col items-end px-5 pt-3.5 pb-5"
                style={{
                  background:
                    "radial-gradient(circle at 50% 56%, rgba(255,255,255,0) 20%, rgba(255,255,255,1) 100%)",
                }}>
                <div className="flex items-center justify-between w-full mb-1">
                  <div className="flex-1" />
                  <div className="flex items-center justify-center" style={{ height: 20 }}>
                    <Icon
                      IconComponent={X}
                      size="sm"
                      className="text-zinc-500 cursor-pointer"
                      onClick={closePopup}
                      iconProps={{ weight: "bold" }}
                    />
                  </div>
                </div>

                <div className="flex items-end justify-between w-full px-1.5">
                  <div className="flex flex-col gap-1">
                    {config.nudge.htmlText && (
                      <span
                        className="text-text-xs-semibold"
                        style={{ color: config.nudge.textColor }}
                        dangerouslySetInnerHTML={{ __html: config.nudge.htmlText }}
                      />
                    )}
                    {config.concessionAmount > 0 && (
                      <div className="flex items-baseline gap-[6px]">
                        <span className="text-[26px] font-bold leading-[33px] tracking-tighter text-zinc-900">
                          {config.amountType === "PERCENTAGE"
                            ? `${config.concessionAmount}%`
                            : `₹${config.concessionAmount}`}
                        </span>
                        <span className="text-[21px] font-semibold leading-[28px] tracking-tighter text-zinc-900/50">
                          off
                        </span>
                      </div>
                    )}
                  </div>

                  {(mrpTotal > 0 || payableTotal > 0) && (
                    <div className="flex flex-col items-end gap-1 pb-1">
                      <div className="flex items-center gap-1">
                        <ShoppingCart
                          size={16}
                          weight="fill"
                          className="text-zinc-800"
                          style={{ borderRadius: "6.4px", opacity: 0.4 }}
                        />
                        <span className="text-text-xs-medium text-zinc-700">{t("your_cart")}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {mrpTotal > payableTotal && (
                          <span className="text-text-sm-medium text-zinc-800 line-through text-center opacity-60">
                            ₹{Number(mrpTotal).toFixed(2)}
                          </span>
                        )}
                        <span className="text-text-sm-bold text-zinc-800 text-center">
                          ₹{Number(payableTotal).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CTA section */}
            <div className="flex flex-col gap-2 px-5 pb-5">
              <button
                type="button"
                className="h-12 w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold text-btnPrimaryText touch-manipulation bg-primary-dark active:[background:color-mix(in_srgb,var(--flo-primary-dark-color)_80%,#71717A_20%)]"
                onClick={handleApplyAndStay}>
                {isLoading ? <DotsLoader color="var(--flo-primary-btn-text-color)" /> : t("apply_offer")}
              </button>
              <button
                type="button"
                className="w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10"
                onClick={onLeaveAttempt}>
                {t("leave_checkout")}
              </button>
            </div>
          </div>
        </div>
      </GenericDialog>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default ExitCouponNudgePopup;
